import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from '../utils/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import './NotificationOverviewModal.css';

const NotificationOverviewModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState(null);
  const [threadId] = useState(uuidv4());
  const { user } = useContext(AuthContext);
  const hasFetchedRef = useRef(false);
  const history = useHistory();

  const handleViewNotifications = () => {
    onClose();
    history.push('/settings/notifications');
  };

  // Fetch overview when modal opens (prevent double call)
  useEffect(() => {
    if (isOpen && user?.id && !hasFetchedRef.current) {
      console.log('NotificationOverviewModal: Fetching overview for user:', user.id);
      hasFetchedRef.current = true;
      fetchNotificationOverview();
    } else {
      console.log('NotificationOverviewModal: Not fetching - isOpen:', isOpen, 'user.id:', user?.id, 'hasFetched:', hasFetchedRef.current);
    }
    
    // Reset the flag when modal closes
    if (!isOpen) {
      hasFetchedRef.current = false;
    }
  }, [isOpen, user?.id]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Format response text to HTML with bold, lists, links, headers, etc.
  const formatResponse = (text) => {
    if (!text) return '';
    
    // Aggressively remove ALL excessive blank lines
    // Replace any sequence of 2+ newlines with just 1 newline
    text = text.replace(/\n\s*\n\s*\n+/g, '\n');
    text = text.replace(/\n\n+/g, '\n');
    
    // Escape HTML to prevent XSS
    let formatted = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Convert markdown links [text](url) - do this BEFORE other formatting
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #ffc20c; text-decoration: underline;">$1</a>');
    
    // Convert markdown-style formatting to HTML
    // Bold: **text** or __text__
    formatted = formatted.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    
    // Italic: *text* or _text_ (but not in already processed HTML)
    formatted = formatted.replace(/\*([^\*<]+)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_([^_<]+)_/g, '<em>$1</em>');
    
    // Split into lines and process
    const lines = formatted.split('\n').filter(line => line.trim()); // Remove all empty lines
    let inList = false;
    let inOrderedList = false;
    const processedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const trimmedLine = lines[i].trim();
      if (!trimmedLine) continue; // Skip any remaining empty lines
      
      // Headers: ### Header or ## Header or # Header
      if (trimmedLine.match(/^#{1,3}\s+(.+)$/)) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        if (inOrderedList) {
          processedLines.push('</ol>');
          inOrderedList = false;
        }
        const headerLevel = trimmedLine.match(/^#{1,3}/)[0].length;
        const headerContent = trimmedLine.replace(/^#{1,3}\s+/, '');
        processedLines.push(`<h${headerLevel}>${headerContent}</h${headerLevel}>`);
      }
      // Unordered list
      else if (trimmedLine.match(/^[-*]\s+(.+)$/)) {
        if (!inList) {
          if (inOrderedList) {
            processedLines.push('</ol>');
            inOrderedList = false;
          }
          processedLines.push('<ul>');
          inList = true;
        }
        const content = trimmedLine.replace(/^[-*]\s+/, '');
        processedLines.push(`<li>${content}</li>`);
      }
      // Ordered list
      else if (trimmedLine.match(/^\d+\.\s+(.+)$/)) {
        if (!inOrderedList) {
          if (inList) {
            processedLines.push('</ul>');
            inList = false;
          }
          processedLines.push('<ol>');
          inOrderedList = true;
        }
        const content = trimmedLine.replace(/^\d+\.\s+/, '');
        processedLines.push(`<li>${content}</li>`);
      }
      // Close lists if needed
      else {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        if (inOrderedList) {
          processedLines.push('</ol>');
          inOrderedList = false;
        }
        
        // Regular paragraph
        processedLines.push(`<p>${trimmedLine}</p>`);
      }
    }
    
    // Close any remaining lists
    if (inList) processedLines.push('</ul>');
    if (inOrderedList) processedLines.push('</ol>');
    
    return processedLines.join('');
  };

  const fetchNotificationOverview = async () => {
    console.log('fetchNotificationOverview called');
    console.log('User ID:', user?.id);
    console.log('Thread ID:', threadId);
    
    setIsLoading(true);
    setError(null);
    setResponse('');
    
    try {
      const formData = new FormData();
      formData.append('input', 'Give me an executive overview of ALL my unread notifications, do NOT provide links to any website screens, do NOT answer in list format it must be in paragraph format AND ONLY URGENT NOTIFICATIONS CAN BE IN LIST FORMAT AFTER THE PARAGRAPH');
      formData.append('thread_id', threadId);
      formData.append('new_chat', 'false');

      console.log('Sending request to /api/offer/hms-interrogation/');
      console.log('FormData contents:', {
        input: 'Give me a full rundown of all my UNREAD notifications and urgent notifications',
        thread_id: threadId,
        new_chat: 'false'
      });

      const result = await axiosInstance.post(
        '/api/offer/hms-interrogation/',
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 second timeout (increased for AI processing)
        }
      );

      console.log('Response received:', result.data);

      if (result.data && result.data.message) {
        setResponse(result.data.message);
        console.log('Response set successfully');
      } else {
        console.log('No message in response');
        setError('No overview available at this time.');
      }
    } catch (err) {
      console.error('Error fetching notification overview:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        response: err.response?.data
      });
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please try again.');
      } else {
        setError('Failed to load notification overview. Please try again.');
      }
    } finally {
      setIsLoading(false);
      console.log('Loading set to false');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="notification-overview-modal-overlay" 
      onClick={onClose}
    >
      <div 
        className="notification-overview-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="notification-overview-header">
          <h3>HAMSTER AI Notifications</h3>
          <button 
            className="notification-overview-close-button" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="notification-overview-body">
          {isLoading && (
            <div className="notification-loader-container">
              <div className="notification-spinner">
                <div className="notification-spinner-ring"></div>
                <div className="notification-spinner-ring"></div>
                <div className="notification-spinner-ring"></div>
                <div className="notification-spinner-center"></div>
              </div>
              <p className="notification-loading-text">Analyzing your notifications...</p>
            </div>
          )}

          {!isLoading && error && (
            <div className="notification-overview-error-state">
              <p className="notification-overview-error-message">{error}</p>
              <button 
                className="notification-overview-retry-button" 
                onClick={fetchNotificationOverview}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && response && (
            <>
              <div 
                className="agent-response-content"
                dangerouslySetInnerHTML={{ __html: formatResponse(response) }}
              />
              <div className="notification-overview-footer">
                <button 
                  className="view-notifications-button"
                  onClick={handleViewNotifications}
                  aria-label="View all notifications"
                >
                  View
                  <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationOverviewModal;

